import { isActiveOnDate } from '@/lib/rrule';
import type { Special } from '@/api/endpoints/bars';

export interface HappyHourGroup {
  timeKey: string;
  displayTime: string;
  specials: Special[];
}

/**
 * Format a time string (HH:MM) to display format (e.g., "3pm", "10:30am")
 */
function formatHHTime(time: string): string {
  const [h, m] = time.split(':').map(Number);
  const mins = m > 0 ? `:${m.toString().padStart(2, '0')}` : '';
  if (h === 0) return `12${mins}am`;
  if (h === 12) return `12${mins}pm`;
  return h > 12 ? `${h - 12}${mins}pm` : `${h}${mins}am`;
}

/**
 * Group happy hour specials by their time window
 * @param specials - All specials for a bar
 * @param date - Date string in YYYY-MM-DD format
 * @returns Array of happy hour groups sorted by start time
 */
export function groupHappyHours(specials: Special[], date: string): HappyHourGroup[] {
  // Filter to only happy hour specials active on selected date
  const hhSpecials = specials.filter(
    (s) => s.isHappyHour && isActiveOnDate(s.rrule, date)
  );

  // Group by time window
  const groups = new Map<string, Special[]>();

  for (const special of hhSpecials) {
    const start = special.happyHourStart || 'open'; // null means "from open"
    const end = special.happyHourEnd || 'close'; // null means "until close"
    const key = `${start}-${end}`;

    const existing = groups.get(key);
    if (existing) {
      existing.push(special);
    } else {
      groups.set(key, [special]);
    }
  }

  // Convert to array and format display times
  return Array.from(groups.entries())
    .map(([timeKey, specials]) => {
      const [start, end] = timeKey.split('-');
      return {
        timeKey,
        displayTime:
          start === 'open' && end === 'close'
            ? 'Open - Close'
            : start === 'open'
              ? `Open - ${formatHHTime(end)}`
              : end === 'close'
                ? `${formatHHTime(start)} - Close`
                : `${formatHHTime(start)} - ${formatHHTime(end)}`,
        specials,
      };
    })
    .sort((a, b) => a.timeKey.localeCompare(b.timeKey));
}
